import React, { useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Container,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider,
} from "@mui/material";
import { LinkedIn, Facebook, Instagram } from "@mui/icons-material";
import {
  Visibility,
  VisibilityOff,
  PersonOutline,
  LockOutlined,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useLoginUserMutation } from "state/api";
import { useDispatch } from "react-redux";
import { login } from "state/index";
import logoImage from "assets/logo.png";

const LoginPage = () => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({ EmpNo: "", password: "" });
  const [errors, setErrors] = useState({ EmpNo: false, password: false });
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [loginUser, { isLoading }] = useLoginUserMutation();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: false });
    setErrorMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      EmpNo: formData.EmpNo.trim() === "",
      password: formData.password.trim() === "",
    };

    setErrors(newErrors);

    if (newErrors.EmpNo || newErrors.password) return;

    try {
      const response = await loginUser(formData).unwrap();

      if (
        response?.token &&
        (response?.user.role[0] === "U" || response?.user.role[0] === "A")
      ) {
        dispatch(
          login({
            token: response.token,
            EmpNo: response.user.id,
            MgCd: response.user.role,
            username: response.user.username,
          })
        );

        const mgCdLength = response.user.role.length;
        if (mgCdLength === 7) {
          window.location.href = "/Mr";
        } else if (mgCdLength === 6 || mgCdLength === 5) {
          window.location.href = "/Sm";
        } else if (response.user.role === "Admi") {
          window.location.href = "/Admin";
        } else {
          setErrorMessage("Unknown role");
        }
      } else {
        setErrorMessage("Invalid credentials or missing token.");
      }
    } catch (err) {
      setErrorMessage(err?.data?.message || "Login Failed");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background:
          "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)",
        p: 2,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: "-50%",
          right: "-10%",
          width: "500px",
          height: "500px",
          background:
            "radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)",
          borderRadius: "50%",
          animation: "float 6s ease-in-out infinite",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: "-50%",
          left: "-10%",
          width: "500px",
          height: "500px",
          background:
            "radial-gradient(circle, rgba(239, 68, 68, 0.15) 0%, transparent 70%)",
          borderRadius: "50%",
          animation: "float 8s ease-in-out infinite reverse",
        },
      }}
    >
      <Container
        component="main"
        maxWidth="lg"
        sx={{ position: "relative", zIndex: 2 }}
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              overflow: "hidden",
              borderRadius: 3,
              backdropFilter: "blur(20px)",
              background: "rgba(15, 23, 42, 0.6)",
              border: "1px solid rgba(59, 130, 246, 0.2)",
              boxShadow:
                "0 20px 60px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
              height: { xs: "auto", md: "600px" },
            }}
          >
            {/* Left Side - Premium Branding */}
            <Box
              sx={{
                flex: 1,
                display: { xs: "none", md: "flex" },
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                p: 8,
                background:
                  "linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(239, 68, 68, 0.1) 100%)",
                color: "white",
                textAlign: "center",
                position: "relative",
                overflow: "hidden",
                borderRight: "1px solid rgba(59, 130, 246, 0.1)",
              }}
            >
              {/* Animated Background Blobs */}
              <Box
                sx={{
                  position: "absolute",
                  top: "20%",
                  left: "10%",
                  width: "200px",
                  height: "200px",
                  background:
                    "radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, transparent 70%)",
                  borderRadius: "50%",
                  filter: "blur(40px)",
                  animation: "blob 8s ease-in-out infinite",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: "20%",
                  right: "10%",
                  width: "250px",
                  height: "250px",
                  background:
                    "radial-gradient(circle, rgba(239, 68, 68, 0.2) 0%, transparent 70%)",
                  borderRadius: "50%",
                  filter: "blur(40px)",
                  animation: "blob 10s ease-in-out infinite reverse",
                }}
              />

              {/* Logo Section */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{
                  delay: 0.2,
                  duration: 0.8,
                  type: "spring",
                  stiffness: 120,
                  damping: 15,
                }}
                style={{ position: "relative", zIndex: 2 }}
              >
                <Box
                  sx={{
                    width: "120px",
                    height: "120px",
                    background: "rgba(59, 130, 246, 0.1)",
                    border: "2px solid rgba(59, 130, 246, 0.3)",
                    borderRadius: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 3,
                    backdropFilter: "blur(10px)",
                    boxShadow: "0 8px 32px rgba(59, 130, 246, 0.1)",
                  }}
                >
                  <Box
                    component="img"
                    src={logoImage}
                    alt="Company Logo"
                    sx={{
                      width: "80px",
                      height: "80px",
                      filter: "drop-shadow(0 4px 12px rgba(59, 130, 246, 0.3))",
                    }}
                  />
                </Box>
              </motion.div>

              {/* Heading */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    background:
                      "linear-gradient(135deg, #3b82f6 0%, #ef4444 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    letterSpacing: "-0.5px",
                  }}
                >
                  KILL THE COMPETITION
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    opacity: 0.8,
                    mb: 4,
                    fontWeight: 300,
                    letterSpacing: "0.5px",
                  }}
                >
                  Jagsonpal Pharmaceuticals
                </Typography>
              </motion.div>

              {/* Stats Cards Grid */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2,
                  width: "100%",
                  position: "relative",
                  zIndex: 2,
                }}
              >
                {[
                  { number: "20", label: "Teams" },
                  { number: "100%", label: "Secure" },
                  { number: "24/7", label: "Support" },
                  { number: "Unify", label: "Brands" },
                ].map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 + index * 0.1, duration: 0.5 }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        background: "rgba(59, 130, 246, 0.05)",
                        border: "1px solid rgba(59, 130, 246, 0.2)",
                        borderRadius: "16px",
                        backdropFilter: "blur(10px)",
                        transition: "all 0.3s ease",
                        cursor: "pointer",
                        "&:hover": {
                          background: "rgba(59, 130, 246, 0.1)",
                          border: "1px solid rgba(59, 130, 246, 0.4)",
                          transform: "translateY(-4px)",
                          boxShadow: "0 8px 20px rgba(59, 130, 246, 0.15)",
                        },
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          background:
                            "linear-gradient(135deg, #3b82f6 0%, #ef4444 100%)",
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        {stat.number}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          opacity: 0.7,
                          fontWeight: 500,
                          letterSpacing: "0.5px",
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </Paper>
                  </motion.div>
                ))}
              </Box>
            </Box>

            {/* Right Side - Login Form */}
            <Box
              sx={{
                flex: 1,
                p: { xs: 3, md: 5 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              {/* Form Header */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    mb: 1,
                    fontWeight: 700,
                    color: "white",
                    letterSpacing: "-0.5px",
                  }}
                >
                  Welcome Back
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 4,
                    color: "rgba(255, 255, 255, 0.6)",
                    fontWeight: 400,
                  }}
                >
                  Sign in to your account to continue
                </Typography>
              </motion.div>

              <form onSubmit={handleSubmit}>
                {/* Employee ID Field */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                >
                  <TextField
                    fullWidth
                    label="Employee ID"
                    name="EmpNo"
                    value={formData.EmpNo}
                    onChange={handleChange}
                    error={errors.EmpNo}
                    helperText={errors.EmpNo ? "Employee ID is required" : ""}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutline
                            sx={{
                              color: "rgba(255, 255, 255, 0.5)",
                              fontSize: "20px",
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    variant="outlined"
                    placeholder="Enter your employee ID"
                    sx={{
                      mb: 2.5,
                      "& .MuiOutlinedInput-root": {
                        color: "white",
                        background: "rgba(59, 130, 246, 0.05)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(59, 130, 246, 0.2)",
                        borderRadius: "12px",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          background: "rgba(59, 130, 246, 0.08)",
                          borderColor: "rgba(59, 130, 246, 0.4)",
                          boxShadow: "0 0 20px rgba(59, 130, 246, 0.1)",
                        },
                        "&.Mui-focused": {
                          background: "rgba(59, 130, 246, 0.1)",
                          borderColor: "#3b82f6",
                          boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "transparent",
                        },
                      },
                      "& .MuiOutlinedInput-input::placeholder": {
                        color: "rgba(255, 255, 255, 0.4)",
                        opacity: 1,
                      },
                      "& .MuiInputLabel-root": {
                        color: "rgba(255, 255, 255, 0.6)",
                        "&.Mui-focused": {
                          color: "#3b82f6",
                        },
                      },
                      "& .MuiFormHelperText-root": {
                        color: "#ef4444",
                        marginTop: "6px",
                      },
                    }}
                  />
                </motion.div>

                {/* Password Field */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                >
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    error={errors.password}
                    helperText={errors.password ? "Password is required" : ""}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined
                            sx={{
                              color: "rgba(255, 255, 255, 0.5)",
                              fontSize: "20px",
                            }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{
                              color: "rgba(255, 255, 255, 0.5)",
                              transition: "color 0.3s ease",
                              "&:hover": {
                                color: "#3b82f6",
                              },
                            }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    variant="outlined"
                    placeholder="Enter your password"
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        color: "white",
                        background: "rgba(59, 130, 246, 0.05)",
                        backdropFilter: "blur(10px)",
                        border: "1px solid rgba(59, 130, 246, 0.2)",
                        borderRadius: "12px",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          background: "rgba(59, 130, 246, 0.08)",
                          borderColor: "rgba(59, 130, 246, 0.4)",
                          boxShadow: "0 0 20px rgba(59, 130, 246, 0.1)",
                        },
                        "&.Mui-focused": {
                          background: "rgba(59, 130, 246, 0.1)",
                          borderColor: "#3b82f6",
                          boxShadow: "0 0 30px rgba(59, 130, 246, 0.2)",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "transparent",
                        },
                      },
                      "& .MuiOutlinedInput-input::placeholder": {
                        color: "rgba(255, 255, 255, 0.4)",
                        opacity: 1,
                      },
                      "& .MuiInputLabel-root": {
                        color: "rgba(255, 255, 255, 0.6)",
                        "&.Mui-focused": {
                          color: "#3b82f6",
                        },
                      },
                      "& .MuiFormHelperText-root": {
                        color: "#ef4444",
                        marginTop: "6px",
                      },
                    }}
                  />
                </motion.div>

                {/* Forgot Password Link */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                >
                  <Box sx={{ textAlign: "right", mb: 3 }}>
                    <Button
                      size="small"
                      sx={{
                        textTransform: "none",
                        color: "#3b82f6",
                        fontSize: "13px",
                        fontWeight: 500,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          color: "#60a5fa",
                          background: "rgba(59, 130, 246, 0.1)",
                        },
                      }}
                    >
                      Forgot password?
                    </Button>
                  </Box>
                </motion.div>

                {/* Error Message */}
                {errorMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        mb: 2.5,
                        background: "rgba(239, 68, 68, 0.1)",
                        border: "1px solid rgba(239, 68, 68, 0.3)",
                        borderRadius: "12px",
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      <Typography
                        color="#ef4444"
                        variant="body2"
                        sx={{
                          textAlign: "center",
                          fontWeight: 500,
                        }}
                      >
                        {errorMessage}
                      </Typography>
                    </Paper>
                  </motion.div>
                )}

                {/* Sign In Button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7, duration: 0.5 }}
                >
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={isLoading}
                    sx={{
                      py: 1.5,
                      borderRadius: "12px",
                      fontWeight: 600,
                      fontSize: "15px",
                      letterSpacing: "0.3px",
                      background:
                        "linear-gradient(135deg, #3b82f6 0%, #ef4444 100%)",
                      textTransform: "uppercase",
                      position: "relative",
                      overflow: "hidden",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxShadow: "0 8px 24px rgba(59, 130, 246, 0.3)",
                      "&:hover:not(:disabled)": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 12px 32px rgba(59, 130, 246, 0.4)",
                      },
                      "&:active:not(:disabled)": {
                        transform: "translateY(0)",
                      },
                      "&:disabled": {
                        opacity: 0.6,
                      },
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Sign In"
                    )}
                  </Button>
                </motion.div>

                {/* Divider */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                >
                  <Box sx={{ my: 3 }}>
                    <Divider sx={{ color: "rgba(255, 255, 255, 0.2)" }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255, 255, 255, 0.5)",
                          fontWeight: 500,
                          letterSpacing: "0.5px",
                        }}
                      >
                        JAGSONPAL SOCIAL
                      </Typography>
                    </Divider>
                  </Box>
                </motion.div>

                {/* Social Media Links */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9, duration: 0.5 }}
                >
                  <Box
                    sx={{ display: "flex", justifyContent: "center", gap: 1.5 }}
                  >
                    {[
                      {
                        icon: <LinkedIn />,
                        url: "https://in.linkedin.com/company/jagsonpalpharmaceuticals",
                        color: "#0A66C2",
                        label: "LinkedIn",
                      },
                      {
                        icon: <Facebook />,
                        url: "https://www.facebook.com/Jagsonpalpharma",
                        color: "#1877F2",
                        label: "Facebook",
                      },
                      {
                        icon: <Instagram />,
                        url: "https://www.instagram.com/jagsonpalpharma",
                        color: "#E4405F",
                        label: "Instagram",
                      },
                    ].map((social, index) => (
                      <motion.div
                        key={social.url}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 1.0 + index * 0.1, duration: 0.4 }}
                      >
                        <IconButton
                          component="a"
                          href={social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={social.label}
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: "12px",
                            background: "rgba(59, 130, 246, 0.1)",
                            border: "1px solid rgba(59, 130, 246, 0.2)",
                            color: social.color,
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            backdropFilter: "blur(10px)",
                            "&:hover": {
                              background: `rgba(${
                                social.color === "#0A66C2"
                                  ? "10, 102, 194"
                                  : social.color === "#1877F2"
                                  ? "24, 119, 242"
                                  : "228, 64, 95"
                              }, 0.2)`,
                              border: `1px solid ${social.color}`,
                              transform: "translateY(-4px)",
                              boxShadow: `0 8px 20px ${social.color}40`,
                            },
                          }}
                        >
                          {social.icon}
                        </IconButton>
                      </motion.div>
                    ))}
                  </Box>
                </motion.div>
              </form>
            </Box>
          </Paper>
        </motion.div>
      </Container>

      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-30px); }
          }
          @keyframes blob {
            0%, 100% { transform: translate(0, 0) scale(1); }
            25% { transform: translate(20px, -50px) scale(1.1); }
            50% { transform: translate(-20px, 20px) scale(0.9); }
            75% { transform: translate(50px, 50px) scale(1.05); }
          }
        `}
      </style>
    </Box>
  );
};

export default LoginPage;
